"use client";

import React from "react";
import { ComponentConfig } from "@puckeditor/core";
import Link from "next/link";
import { businessData } from "@/data/business";
import { ImagePicker } from "@/components/ImagePicker";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

export interface CategoryItem {
  id: string;
  title: string;
  tagline: string;
  image: string;
  href: string;
  icon?: string;
}

export interface CategoryGridProps {
  heading?: string;
  subheading?: string;
  categories?: CategoryItem[];
}

export const CategoryGridBlock: ComponentConfig<CategoryGridProps> = {
  label: "Product Category Grid",
  fields: {
    heading: { type: "text", label: "Section Heading" },
    subheading: { type: "textarea", label: "Section Subheading" },
    categories: {
      type: "array",
      getItemSummary: (cat: any) => cat.title || "Category",
      arrayFields: {
        id: { type: "text", label: "ID / Slug" },
        title: { type: "text", label: "Category Title" },
        tagline: { type: "textarea", label: "Tagline / Description" },
        image: { type: "custom", label: "Thumbnail Image", render: ({ value, onChange }: any) => <ImagePicker value={value} onChange={onChange} /> },
        href: { type: "text", label: "Page Link Route" },
        icon: {
          type: "select",
          label: "Category Icon",
          options: [
            { label: "Skin / sparkle", value: "sparkle" },
            { label: "Hair / botanical", value: "hair" },
            { label: "Body / drops", value: "drop" },
            { label: "Baby / heart", value: "baby" },
            { label: "Men / performance", value: "male" },
            { label: "Suncare / shield", value: "sun" },
            { label: "Oral care", value: "tooth" },
            { label: "Pet care", value: "paw" },
            { label: "Kits / gift", value: "gift" },
          ],
        },
      },
      defaultItemProps: {
        id: "new-formulation-suite",
        title: "New Formulation Suite",
        tagline: "Describe the products, users, and formulation direction covered by this category.",
        image: "/assets/categories/skin-care-v2.jpg",
        href: "/product-range",
        icon: "sparkle",
      },
    },
  },
  defaultProps: {
    heading: "Engineered for Active Stability & Sensory Excellence",
    subheading: "Explore seven specialized formulation suites engineered for active stability, sensory excellence, and regulatory compliance.",
    categories: businessData.categories,
  },
  render: ({ heading, subheading, categories }) => {
    return (
      <section className="sec sec-alt" id="categories">
        <div className="container">
          <div className="sec-head">
            <h2>{heading}</h2>
            <p className="sec-sub">{subheading}</p>
          </div>
          <div className="cat-grid">
            {(categories || []).map((cat) => (
              <Link className="cat-card" href={cat.href || `#${cat.id}`} key={cat.id} data-animate>
                <div className="cat-card-thumb">
                  <img src={resolveMediaUrl(cat.image, "/assets/categories/skin-care-v2.jpg")} alt={`${cat.title} contract manufacturing`} loading="lazy" />
                  <span className="cat-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {cat.icon === "hair" ? (
                        <>
                          <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
                          <path d="M2 22 12 12" />
                        </>
                      ) : cat.icon === "drop" ? (
                        <>
                          <path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.76 7 5.3c-.29 1.46-.58 2.3-1.71 3.23S3.58 11.1 3.58 12.25c0 2.22 1.8 4.05 4 4.05z" />
                          <path d="M17 21c3.31 0 6-2.63 6-5.87 0-1.68-.83-3.27-2.48-4.63S17.42 7.76 17 5.65c-.42 2.11-.84 3.32-2.48 4.68S12.04 13.45 12.04 15.13C12.04 18.37 14.69 21 17 21z" />
                        </>
                      ) : cat.icon === "baby" ? (
                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                      ) : cat.icon === "male" ? (
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                      ) : cat.icon === "sun" ? (
                        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
                      ) : cat.icon === "tooth" ? (
                        <>
                          <path d="M12 3c4 4 6 8 6 11a6 6 0 0 1-12 0c0-3 2-7 6-11z" />
                          <path d="M9 14h6" />
                        </>
                      ) : cat.icon === "paw" ? (
                        <>
                          <circle cx="11" cy="4" r="2" />
                          <circle cx="18" cy="8" r="2" />
                          <circle cx="20" cy="16" r="2" />
                          <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.09Q6.1 17.9 5 16.5q-1.1-1.4-.26-3.41A5 5 0 0 1 9 10z" />
                        </>
                      ) : cat.icon === "gift" ? (
                        <>
                          <rect x="3" y="8" width="18" height="13" rx="2" />
                          <path d="M12 8v13M3 12h18M12 8C10 8 7 4.5 9 3s3 3 3 5z" />
                          <path d="M12 8C14 8 17 4.5 15 3s-3 3-3 5z" />
                        </>
                      ) : (
                        <>
                          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                          <path d="M5 3v4M19 17v4M3 5h4M17 19h4" />
                        </>
                      )}
                    </svg>
                  </span>
                </div>
                <div className="cat-card-body">
                  <h3>{cat.title}</h3>
                  <p>{cat.tagline}</p>
                  <span className="cat-more">
                    Explore Formulation Suite{" "}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <p className="sec-cta">
            <Link href="/product-range">
              Explore all formulation categories{" "}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </p>
        </div>
      </section>
    );
  },
};
